// body_measurement schema + site catalog + derived math + Bedrock tool
// (BODY_MEASUREMENTS_PLAN.md, D-MEAS-1..19). One record per measuring session:
// tape-measure circumferences at a fixed set of anatomical sites, stored
// canonically in centimeters so all math (ratios, deltas, the Navy estimate)
// reads a single source of truth. Mirrors the "one catalog is the single
// source of truth" pattern in bodyComposition.ts, and the canonical/display
// unit split in height.ts / weight.ts.

import type { Patient } from "./types.js";

// ─── Display/entry unit ──────────────────────────────────────────────────────

/** Display/entry unit for circumferences. Values stay canonical in `cm`. */
export const MEASUREMENT_DISPLAY_UNITS = ["in", "cm"] as const;
export type MeasurementDisplayUnit = (typeof MEASUREMENT_DISPLAY_UNITS)[number];

export const MEASUREMENT_UNIT_LABELS: Record<MeasurementDisplayUnit, string> = {
  in: "Inches (in)",
  cm: "Centimeters (cm)",
};

/** Default when the patient hasn't chosen one (US household, matches weight's "lbs"). */
export const DEFAULT_MEASUREMENT_UNIT: MeasurementDisplayUnit = "in";

const CM_PER_INCH = 2.54;

/** Convert inches to canonical centimeters, rounded to two decimal places
 *  (matches weight.ts's lbsToKg — display-to-canonical rounds tighter than
 *  canonical-to-display, so a round-trip through the display unit never
 *  drifts the stored value). */
export function inToCm(inches: number): number {
  return Math.round(inches * CM_PER_INCH * 100) / 100;
}

/** Convert canonical centimeters to inches, rounded to one decimal place
 *  (matches weight.ts's kgToLbs). */
export function cmToIn(cm: number): number {
  return Math.round((cm / CM_PER_INCH) * 10) / 10;
}

/** Convert a canonical cm value to the patient's preferred display value. */
export function toDisplay(cm: number, unit: MeasurementDisplayUnit): number {
  return unit === "in" ? cmToIn(cm) : Math.round(cm * 10) / 10;
}

/** Convert a display value back to canonical centimeters. */
export function fromDisplay(value: number, unit: MeasurementDisplayUnit): number {
  return unit === "in" ? inToCm(value) : Math.round(value * 100) / 100;
}

/** Format a canonical cm value in the patient's preferred unit, e.g.
 *  "34.0 in" or "86.4 cm". */
export function formatMeasurement(cm: number, unit: MeasurementDisplayUnit): string {
  const v = toDisplay(cm, unit);
  return `${v} ${unit}`;
}

// ─── Sources ──────────────────────────────────────────────────────────────

/** How one site's value got into the record. */
export const MEASUREMENT_SOURCES = ["manual", "import", "chat"] as const;
export type MeasurementSource = (typeof MEASUREMENT_SOURCES)[number];

// ─── Site catalog ────────────────────────────────────────────────────────────

export const MEASUREMENT_GROUPS = ["torso", "arms", "legs", "custom"] as const;
export type MeasurementGroup = (typeof MEASUREMENT_GROUPS)[number];

export interface MeasurementSiteDef {
  id: string;
  label: string; // short, for the form row
  group: MeasurementGroup;
  laterality: "none" | "left" | "right";
  /** Sites that share one anatomical location and therefore one tape band on the
   *  map (relaxed vs flexed bicep). The band highlights when EITHER is focused. */
  bandGroup: string;
  min: number; // cm, plausibility floor
  max: number; // cm, plausibility ceiling
  /** Curated guidance — the ⓘ card. Three fields, always all three (D-MEAS-6). */
  landmark: string; // where exactly the tape goes
  avoid: string; // the mistake that ruins comparability
  posture: string; // how to stand / what to do with the limb
  /** Whether this site feeds a derived readout, shown as a badge on the form row. */
  usedIn?: ("whr" | "whtr" | "navy")[];
}

/**
 * The built-in site catalog — 23 sites (BODY_MEASUREMENTS_PLAN.md §3). Curated
 * guidance text is authored once here, deterministically, so the wording is
 * identical session to session — consistency is what makes the numbers
 * comparable. No Bedrock call, no cache table (D-MEAS-6).
 */
export const MEASUREMENT_SITES: readonly MeasurementSiteDef[] = [
  // ── Torso ──
  {
    id: "neck",
    label: "Neck",
    group: "torso",
    laterality: "none",
    bandGroup: "neck",
    min: 20,
    max: 70,
    landmark:
      "Wrap the tape around the middle of your neck, just below the Adam's apple, keeping it level all the way around.",
    avoid:
      "Don't measure over a collar, don't angle the tape up under the jaw, and don't let it slide down onto your shoulders.",
    posture: "Stand tall, look straight ahead, shoulders relaxed. Don't tuck your chin.",
    usedIn: ["navy"],
  },
  {
    id: "shoulders",
    label: "Shoulders",
    group: "torso",
    laterality: "none",
    bandGroup: "shoulders",
    min: 70,
    max: 200,
    landmark: "Around the widest part of your shoulders, across the fullest part of both deltoids.",
    avoid:
      "Don't raise, shrug, or flex your shoulders, and don't let the tape ride up toward your neck or down onto your chest.",
    posture: "Arms hanging relaxed at your sides, normal breathing.",
  },
  {
    id: "chest",
    label: "Chest / Bust",
    group: "torso",
    laterality: "none",
    bandGroup: "chest",
    min: 50,
    max: 200,
    landmark: "Around the fullest part of your chest, at nipple level, with the tape level across your back.",
    avoid: "Don't puff your chest, don't hold a deep breath, and don't let the tape run diagonally.",
    posture: "Arms down, breathe out normally, then measure.",
  },
  {
    id: "waist_narrow",
    label: "Waist (narrowest)",
    group: "torso",
    laterality: "none",
    bandGroup: "waist_narrow",
    min: 40,
    max: 200,
    landmark:
      "The narrowest part of your torso, between the bottom of your ribs and the top of your hip bones — usually just above the navel. Tape snug and parallel to the floor.",
    avoid:
      "Don't suck in, don't measure over thick clothing, and don't pick a different height each time. If you can't find a narrow point, use the Abdomen site instead and stay with it.",
    posture: "Stand relaxed, feet together, breathe out normally before reading.",
    usedIn: ["whr", "whtr", "navy"],
  },
  {
    id: "abdomen",
    label: "Abdomen (navel)",
    group: "torso",
    laterality: "none",
    bandGroup: "abdomen",
    min: 40,
    max: 200,
    landmark: "Around the broadest part of your torso at the level of your navel.",
    avoid:
      "Don't compress the belly with a tight tape, and don't switch between this and the narrowest waist between sessions.",
    posture: "Stand straight, feet together, arms at your sides, normal exhale.",
    usedIn: ["navy"],
  },
  {
    id: "waist_iliac",
    label: "Waist (iliac crest)",
    group: "torso",
    laterality: "none",
    bandGroup: "waist_iliac",
    min: 40,
    max: 200,
    landmark:
      "A horizontal line at the top edge of your hip bone (iliac crest) — feel for the bony ridge at your side and measure level with its top. This is the WHO / NIH protocol.",
    avoid:
      "Don't measure above your lowest rib or below the hip bone, and don't guess the landmark — feel for it every time.",
    posture: "Stand relaxed, feet together, arms crossed on your chest, measure at the end of a normal exhale.",
  },
  {
    id: "hips",
    label: "Hips",
    group: "torso",
    laterality: "none",
    bandGroup: "hips",
    min: 50,
    max: 200,
    landmark: "The widest point around your buttocks, with the tape level all the way around.",
    avoid:
      "Don't measure at your waist or down on your thighs, and don't let the tape dip or rise at the back — check it in a mirror.",
    posture: "Feet together, weight even on both feet.",
    usedIn: ["whr", "navy"],
  },
  // ── Arms ──
  ...makeLateralPair({
    idBase: "bicep_relaxed",
    label: "Bicep, relaxed",
    bandGroupBase: "bicep",
    min: 15,
    max: 70,
    landmark:
      "The largest part of your upper arm, roughly halfway between shoulder and elbow, with the arm hanging relaxed.",
    avoid: "Don't flex — that's the separate flexed site. Don't measure over a sleeve, and don't change the height between sessions.",
    posture: "Arm hanging straight down at your side, muscles relaxed.",
  }),
  ...makeLateralPair({
    idBase: "bicep_flexed",
    label: "Bicep, flexed",
    bandGroupBase: "bicep",
    min: 15,
    max: 75,
    landmark: "The largest part of your upper arm with the bicep fully contracted.",
    avoid: "Don't record a flexed number in the relaxed field — they are different sites on purpose.",
    posture: "Arm raised to shoulder height, elbow bent to about 90°, fist clenched, bicep squeezed hard.",
  }),
  ...makeLateralPair({
    idBase: "forearm",
    label: "Forearm",
    bandGroupBase: "forearm",
    min: 12,
    max: 55,
    landmark: "The largest part of your forearm, usually a few centimetres below the elbow.",
    avoid: "Don't measure near the wrist, and don't change how far the elbow is bent.",
    posture: "Arm hanging relaxed at your side, hand open.",
  }),
  ...makeLateralPair({
    idBase: "wrist",
    label: "Wrist",
    bandGroupBase: "wrist",
    min: 10,
    max: 30,
    landmark: "Around the wrist joint, just past the bony bumps, on the hand side of them.",
    avoid:
      "Don't measure up on the forearm. Wrist size barely changes — a big swing usually means the tape moved, not that you did.",
    posture: "Arm relaxed, hand open, palm up.",
  }),
  // ── Legs ──
  ...makeLateralPair({
    idBase: "thigh_upper",
    label: "Thigh, upper",
    bandGroupBase: "thigh_upper",
    min: 25,
    max: 110,
    landmark: "The largest part of your upper thigh, about 2-3 cm below the crease where your buttock meets your thigh.",
    avoid: "Don't measure over leggings, don't drift up into the hip, and don't drift down toward the knee.",
    posture: "Stand with feet slightly apart and your weight even on both legs.",
  }),
  ...makeLateralPair({
    idBase: "thigh_mid",
    label: "Thigh, mid",
    bandGroupBase: "thigh_mid",
    min: 22,
    max: 100,
    landmark: "Halfway between the crease where your thigh meets your trunk and the top of your kneecap, with the tape horizontal.",
    avoid: "Don't measure sitting down, and don't let the knee bend a different amount each time.",
    posture: "Standing, knee straight, weight even on both legs.",
  }),
  ...makeLateralPair({
    idBase: "calf",
    label: "Calf",
    bandGroupBase: "calf",
    min: 20,
    max: 70,
    landmark: "The widest part of your calf, between knee and ankle.",
    avoid: "Don't measure above or below the widest point, and don't rise onto your toes or flex the calf.",
    posture: "Standing, weight even on both feet, calf relaxed.",
  }),
  ...makeLateralPair({
    idBase: "ankle",
    label: "Ankle",
    bandGroupBase: "ankle",
    min: 12,
    max: 40,
    landmark: "The narrowest point above the ankle bones.",
    avoid: "Don't measure across the ankle bones themselves. Ankle size rising over days can reflect fluid — note it, don't diagnose it.",
    posture: "Standing, weight even on both feet.",
  }),
];

/** Builds a left/right pair of arm/leg site definitions from one shared spec,
 *  so the label/landmark/avoid/posture text can never drift between sides. */
function makeLateralPair(spec: {
  idBase: string;
  label: string;
  bandGroupBase: string;
  min: number;
  max: number;
  landmark: string;
  avoid: string;
  posture: string;
  usedIn?: ("whr" | "whtr" | "navy")[];
}): MeasurementSiteDef[] {
  const group: MeasurementGroup = spec.idBase.startsWith("thigh") || spec.idBase === "calf" || spec.idBase === "ankle"
    ? "legs"
    : "arms";
  return (["left", "right"] as const).map((side) => ({
    id: `${spec.idBase}_${side === "left" ? "l" : "r"}`,
    label: `${spec.label} (${side === "left" ? "L" : "R"})`,
    group,
    laterality: side,
    bandGroup: `${spec.bandGroupBase}_${side === "left" ? "l" : "r"}`,
    min: spec.min,
    max: spec.max,
    landmark: spec.landmark,
    avoid: spec.avoid,
    posture: spec.posture,
    usedIn: spec.usedIn,
  }));
}

/** Fast lookup of a built-in site definition by id. */
export const MEASUREMENT_SITE_BY_ID: Record<string, MeasurementSiteDef> = Object.fromEntries(
  MEASUREMENT_SITES.map((s) => [s.id, s]),
);

export const MEASUREMENT_SITE_IDS = MEASUREMENT_SITES.map((s) => s.id);

// ─── Custom sites ────────────────────────────────────────────────────────────

/** User-defined measurement sites beyond the built-in catalog (D-MEAS-16). Ids
 *  are always prefixed "custom/" so a future built-in id can never collide. */
export interface CustomMeasurementSite {
  id: string; // always starts "custom/"
  label: string; // 1-40 chars
  group: MeasurementGroup;
  laterality: "none" | "left" | "right";
  landmark?: string; // the user's own note on where they measure it
}

export const MAX_CUSTOM_MEASUREMENT_SITES = 8;
export const DEFAULT_MEASUREMENT_REMINDER_DAYS = 30;

const GROUP_ORDER: Record<MeasurementGroup, number> = { torso: 0, arms: 1, legs: 2, custom: 3 };

/** Returns built-in sites plus the patient's custom sites, in stable display
 *  order: torso → arms → legs → custom, and within a group, catalog order. */
export function resolveMeasurementSites(
  patient: Pick<Patient, "customMeasurementSites"> | undefined,
): (MeasurementSiteDef | CustomMeasurementSite)[] {
  const custom = patient?.customMeasurementSites ?? [];
  return [...MEASUREMENT_SITES, ...custom].slice().sort((a, b) => GROUP_ORDER[a.group] - GROUP_ORDER[b.group]);
}

// ─── Payload ─────────────────────────────────────────────────────────────────

/** One site's reading within a session. */
export interface BodyMeasurementValue {
  /** Canonical centimeters. The ONLY field any math reads. */
  cm: number;
  /** What the user actually typed / the file actually carried, for round-trip fidelity. */
  enteredUnit: MeasurementDisplayUnit;
  source: MeasurementSource;
  /** Optional per-site note, e.g. "tape felt loose". */
  note?: string;
}

/** Conditions the whole session was taken under. Circumferences swing by
 *  several cm across a day (food, fluid, pump), so recording this is what
 *  makes two sessions comparable — and it is also what the vision model
 *  needs to interpret a photo. */
export interface MeasurementConditions {
  timeOfDay?: "morning" | "midday" | "evening";
  fasted?: boolean;
  postWorkout?: boolean;
}

/** Payload for a `body_measurement` event. One event = one measuring session. */
export interface BodyMeasurementPayload {
  measuredAt: string; // R — ISO-8601 with offset
  sites: Record<string, BodyMeasurementValue>; // R — at least one entry
  heightCmAtTime?: number; // snapshot, so WHtR stays correct if the profile changes later
  weightKgAtTime?: number; // snapshot
  conditions?: MeasurementConditions;
  /** Import-profile id when this session came from a file, e.g. "renpho_tape". */
  importSource?: string;
  notes?: string;
}

// ─── Bedrock chat-capture tool ────────────────────────────────────────────────

/**
 * Bedrock tool: record a tape-measuring session from chat. Mirrors
 * RECORD_BODY_COMPOSITION_TOOL's shape (bodyComposition.ts). The model must
 * state the unit the user used and must never convert it itself — the API
 * validator does the conversion to canonical cm.
 */
export const RECORD_BODY_MEASUREMENT_TOOL = {
  name: "record_body_measurement",
  description:
    "Record a tape-measure circumference session the user reports (e.g. 'my waist is 34 inches'). " +
    "These are the user's own tape measurements, not estimates. Report the unit the user actually " +
    "used and the value exactly as spoken — never convert or infer the unit yourself.",
  input_schema: {
    type: "object",
    properties: {
      measuredAt: { type: "string", description: "ISO-8601 with UTC offset. Omit if no time was given." },
      unit: {
        type: "string",
        enum: [...MEASUREMENT_DISPLAY_UNITS],
        description: "The unit the user used for every value below. Required — never infer this.",
      },
      sites: {
        type: "array",
        description: "One entry per site the user reported.",
        items: {
          type: "object",
          properties: {
            id: { type: "string", enum: [...MEASUREMENT_SITE_IDS], description: "Site id from the catalog." },
            value: { type: "number" },
          },
          required: ["id", "value"],
        },
      },
      conditions: {
        type: "object",
        properties: {
          timeOfDay: { type: "string", enum: ["morning", "midday", "evening"] },
          fasted: { type: "boolean" },
          postWorkout: { type: "boolean" },
        },
      },
      notes: { type: "string" },
    },
    required: ["unit", "sites"],
  },
} as const;

// ─── Derived readouts ─────────────────────────────────────────────────────────

function waistCm(sites: Record<string, BodyMeasurementValue>): number | undefined {
  return sites.waist_narrow?.cm ?? sites.abdomen?.cm;
}

/** Waist-to-hip ratio. Prefers the narrowest waist; falls back to abdomen.
 *  Returns null if either input is missing or hips <= 0. */
export function waistToHipRatio(sites: Record<string, BodyMeasurementValue>): number | null {
  const waist = waistCm(sites);
  const hips = sites.hips?.cm;
  if (waist == null || hips == null || hips <= 0) return null;
  return Math.round((waist / hips) * 1000) / 1000;
}

/** Waist-to-height ratio. Needs a height (payload snapshot, else patient
 *  profile). Returns null if either input is missing or height <= 0. */
export function waistToHeightRatio(
  sites: Record<string, BodyMeasurementValue>,
  heightCm: number | undefined,
): number | null {
  const waist = waistCm(sites);
  if (waist == null || heightCm == null || heightCm <= 0) return null;
  return Math.round((waist / heightCm) * 1000) / 1000;
}

/**
 * US-Navy body-fat estimate (metric form). A population regression, NOT a
 * measurement — always rendered with that caveat (D-MEAS-17).
 *
 * The Navy protocol measures men at the NAVEL and women at the NARROWEST
 * waist, so the two formulas take different sites on purpose; each falls
 * back to the other site only if its own is missing.
 *
 *   male:   495 / (1.0324 - 0.19077*log10(waist - neck) + 0.15456*log10(height)) - 450
 *   female: 495 / (1.29579 - 0.35004*log10(waist + hip - neck) + 0.22100*log10(height)) - 450
 *
 * Returns null when: sex is not "male"/"female" (D-MEAS-6 assumption 6), any
 * required site is missing, height is missing, the log10 argument is <= 0, or
 * the result falls outside [2, 70].
 */
export function navyBodyFatPct(args: {
  sites: Record<string, BodyMeasurementValue>;
  heightCm: number | undefined;
  sex: Patient["sex"];
}): number | null {
  const { sites, heightCm, sex } = args;
  if (heightCm == null || heightCm <= 0) return null;
  const neck = sites.neck?.cm;
  if (neck == null) return null;

  let result: number;
  if (sex === "male") {
    const waist = sites.abdomen?.cm ?? sites.waist_narrow?.cm;
    if (waist == null) return null;
    const arg = waist - neck;
    if (arg <= 0) return null;
    const denom = 1.0324 - 0.19077 * Math.log10(arg) + 0.15456 * Math.log10(heightCm);
    if (denom === 0) return null;
    result = 495 / denom - 450;
  } else if (sex === "female") {
    const waist = sites.waist_narrow?.cm ?? sites.abdomen?.cm;
    const hip = sites.hips?.cm;
    if (waist == null || hip == null) return null;
    const arg = waist + hip - neck;
    if (arg <= 0) return null;
    const denom = 1.29579 - 0.35004 * Math.log10(arg) + 0.221 * Math.log10(heightCm);
    if (denom === 0) return null;
    result = 495 / denom - 450;
  } else {
    return null;
  }

  if (!Number.isFinite(result) || result < 2 || result > 70) return null;
  return Math.round(result * 10) / 10;
}

/** Hedged reference-band kinds for the badge next to a ratio readout. */
export type MeasurementRatioKind = "whr" | "whtr";

/** Population reference band label for a ratio readout, per §4's table. Not a
 *  diagnosis — "general population reference" framing only (D-MEAS-17). */
export function measurementRatioBand(
  kind: MeasurementRatioKind,
  value: number | null,
  sex: Patient["sex"],
): string | null {
  if (value == null) return null;
  if (kind === "whtr") {
    if (value < 0.4) return "Below typical range";
    if (value < 0.5) return "Typical range";
    if (value < 0.6) return "Above typical range";
    return "Well above typical range";
  }
  // whr
  const threshold = sex === "female" ? 0.85 : 0.9;
  return value < threshold ? "Typical range" : "Above typical range";
}

/** Per-site change vs. the immediately previous session and vs. the first session. */
export interface MeasurementDelta {
  siteId: string;
  currentCm: number;
  previousCm?: number;
  firstCm?: number;
  vsPreviousCm?: number; // current - previous
  vsFirstCm?: number; // current - first
}

/** Builds deltas for the LATEST session in `sessions` (any order; sorted
 *  internally by `measuredAt`). A site present only in the latest session has
 *  `vsPreviousCm`/`vsFirstCm` left `undefined` — a missing earlier reading is
 *  not "no change". */
export function buildMeasurementDeltas(sessions: BodyMeasurementPayload[]): MeasurementDelta[] {
  if (sessions.length === 0) return [];
  const sorted = sessions.slice().sort((a, b) => a.measuredAt.localeCompare(b.measuredAt));
  const latest = sorted[sorted.length - 1]!;
  const previous = sorted.length >= 2 ? sorted[sorted.length - 2]! : undefined;
  const first = sorted[0]!;

  return Object.entries(latest.sites).map(([siteId, value]) => {
    const previousCm = previous?.sites[siteId]?.cm;
    // "first" excludes the latest session itself unless it is the only session.
    const firstCm = first !== latest ? first.sites[siteId]?.cm : undefined;
    const delta: MeasurementDelta = {
      siteId,
      currentCm: value.cm,
    };
    if (previousCm != null) {
      delta.previousCm = previousCm;
      delta.vsPreviousCm = Math.round((value.cm - previousCm) * 100) / 100;
    }
    if (firstCm != null) {
      delta.firstCm = firstCm;
      delta.vsFirstCm = Math.round((value.cm - firstCm) * 100) / 100;
    }
    return delta;
  });
}
